import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, ChevronUp, ChevronDown, Check } from 'lucide-react';

const API_ROOT = (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin);
const API = API_ROOT + '/api';

// Wall-by-wall hang list for wallpaper: break each wallpapered room into its
// individual walls with per-wall measurements and a hang order.
const WallpaperHangList = ({ projectId, projectName = '', onClose }) => {
  const [walls, setWalls] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [hl, rm] = await Promise.all([
          fetch(`${API}/projects/${projectId}/wallpaper-hang-list`).then((r) => r.json()),
          fetch(`${API}/projects/${projectId}/wallpaper-rooms`).then((r) => r.json()),
        ]);
        setWalls((hl.walls || []).sort((a, b) => (a.hang_order || 0) - (b.hang_order || 0)));
        const wp = rm.wallpaper_rooms || [];
        const all = rm.all_rooms || [];
        setRooms(wp.length ? wp : all);
      } catch (e) {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  const addWall = (roomName = '') =>
    setWalls((prev) => {
      setSavedAt(null);
      return [
      ...prev,
      {
        id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        room_name: roomName || rooms[0] || '',
        label: '',
        width_ft: '',
        height_ft: '',
        hang_order: prev.length + 1,
        notes: '',
        done: false,
      },
    ];
    });

  const update = (id, patch) => { setSavedAt(null); setWalls((prev) => prev.map((w) => (w.id === id ? { ...w, ...patch } : w))); };
  const remove = (id) => { setSavedAt(null); setWalls((prev) => prev.filter((w) => w.id !== id).map((w, k) => ({ ...w, hang_order: k + 1 }))); };
  const move = (id, dir) => {
      setSavedAt(null);
      setWalls((prev) => {
      const i = prev.findIndex((w) => w.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const arr = [...prev];
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr.map((w, k) => ({ ...w, hang_order: k + 1 }));
    });
    };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        walls: walls.map((w, i) => ({
          id: String(w.id).startsWith('tmp-') ? undefined : w.id,
          room_name: w.room_name,
          label: w.label,
          width_ft: w.width_ft === '' ? null : Number(w.width_ft),
          height_ft: w.height_ft === '' ? null : Number(w.height_ft),
          hang_order: i + 1,
          notes: w.notes,
          done: w.done,
        })),
      };
      const res = await fetch(`${API}/projects/${projectId}/wallpaper-hang-list`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      setWalls((data.walls || []).sort((a, b) => (a.hang_order || 0) - (b.hang_order || 0)));
      setSavedAt(new Date());
    } catch (e) {
      alert('Could not save the hang list: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const sqft = (w) => {
    const a = Number(w.width_ft || 0) * Number(w.height_ft || 0);
    return a > 0 ? Math.round(a) : '';
  };

  // Group for display, keeping global hang order
  const groups = [];
  walls.forEach((w) => {
    const key = w.room_name || 'Unassigned';
    let g = groups.find((x) => x.room === key);
    if (!g) { g = { room: key, list: [] }; groups.push(g); }
    g.list.push(w);
  });

  const inputCls = 'w-full px-2 py-1 bg-stone-800 border border-stone-600 rounded text-white text-xs focus:outline-none focus:border-[#D4A574]';

  return createPortal(
    <div data-testid="wallpaper-hang-list-overlay" onClick={onClose} className="fixed inset-0 z-[100000] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div onClick={(e) => e.stopPropagation()} className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-stone-700 bg-stone-900 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4" style={{ background: 'linear-gradient(90deg,#B45309,#92400E)' }}>
          <div>
            <h2 className="text-lg font-bold text-white">📐 Wallpaper Hang List{projectName ? ` · ${projectName}` : ''}</h2>
            <p className="text-xs text-white/80">Every wall to paper, in the order it gets hung — with measurements.</p>
          </div>
          <button data-testid="wallpaper-hang-list-close" onClick={onClose} className="text-white/80 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="py-10 text-center text-sm text-stone-400">Loading…</div>
          ) : walls.length === 0 ? (
            <div className="py-10 text-center">
              <p className="mb-4 text-sm text-stone-400">No walls yet. Add the first wall to start the hang order.</p>
              <button data-testid="wallpaper-add-first-wall" onClick={() => addWall()} className="inline-flex items-center gap-2 rounded-lg bg-[#B45309] px-4 py-2 text-sm font-bold text-white hover:bg-[#92400E]">
                <Plus className="h-4 w-4" /> Add first wall
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {groups.map((g) => (
                <div key={g.room} className="rounded-lg border border-stone-700">
                  <div className="flex items-center justify-between border-b border-stone-700 bg-stone-800/60 px-3 py-2">
                    <span className="text-sm font-bold text-[#D4A574]">{g.room}</span>
                    <span className="text-[11px] text-stone-400">
                      {g.list.length} wall{g.list.length === 1 ? '' : 's'} · {Math.round(g.list.reduce((s, w) => s + Number(w.width_ft || 0) * Number(w.height_ft || 0), 0))} sq ft
                    </span>
                  </div>
                  <div className="divide-y divide-stone-800">
                    {g.list.map((w) => (
                      <div key={w.id} data-testid={`wall-row-${w.id}`} className="grid grid-cols-12 items-center gap-2 px-3 py-2">
                        <div className="col-span-1 flex flex-col items-center">
                          <span className="mb-1 text-[11px] font-bold text-stone-400">#{w.hang_order}</span>
                          <div className="flex flex-col">
                            <button onClick={() => move(w.id, -1)} title="Move up" className="text-stone-500 hover:text-white"><ChevronUp className="h-3 w-3" /></button>
                            <button onClick={() => move(w.id, 1)} title="Move down" className="text-stone-500 hover:text-white"><ChevronDown className="h-3 w-3" /></button>
                          </div>
                        </div>
                        <div className="col-span-3">
                          <input data-testid={`wall-label-${w.id}`} value={w.label} onChange={(e) => update(w.id, { label: e.target.value })} placeholder="Wall (e.g. North / behind bed)" className={inputCls} />
                        </div>
                        <div className="col-span-2">
                          <select value={w.room_name} onChange={(e) => update(w.id, { room_name: e.target.value })} className={inputCls}>
                            {[w.room_name, ...rooms.filter((r) => r !== w.room_name)].filter(Boolean).map((r) => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-1">
                          <input data-testid={`wall-width-${w.id}`} type="number" step="0.1" value={w.width_ft} onChange={(e) => update(w.id, { width_ft: e.target.value })} placeholder="W ft" className={inputCls} />
                        </div>
                        <div className="col-span-1">
                          <input data-testid={`wall-height-${w.id}`} type="number" step="0.1" value={w.height_ft} onChange={(e) => update(w.id, { height_ft: e.target.value })} placeholder="H ft" className={inputCls} />
                        </div>
                        <div className="col-span-1 text-center text-[11px] text-stone-400">{sqft(w) ? `${sqft(w)} sf` : '—'}</div>
                        <div className="col-span-2 flex items-center gap-1">
                          <input value={w.notes} onChange={(e) => update(w.id, { notes: e.target.value })} placeholder="Notes" className={inputCls} />
                          <button onClick={() => update(w.id, { done: !w.done })} title={w.done ? 'Hung' : 'Mark hung'} className={`rounded p-1 ${w.done ? 'bg-green-600 text-white' : 'bg-stone-800 text-stone-400 hover:text-white'}`}><Check className="h-3 w-3" /></button>
                          <button onClick={() => remove(w.id)} title="Remove wall" className="rounded p-1 text-stone-500 hover:bg-red-700 hover:text-white"><Trash2 className="h-3 w-3" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-stone-800 px-3 py-2">
                    <button data-testid={`wallpaper-add-wall-${g.room}`} onClick={() => addWall(g.room)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#D4A574] hover:text-white">
                      <Plus className="h-3 w-3" /> Add wall to {g.room}
                    </button>
                  </div>
                </div>
              ))}
              <button data-testid="wallpaper-add-wall" onClick={() => addWall()} className="inline-flex items-center gap-2 rounded-lg border border-stone-700 px-3 py-2 text-xs font-semibold text-stone-300 hover:bg-stone-800">
                <Plus className="h-4 w-4" /> Add another wall
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-stone-700 bg-stone-800 p-4">
          <span className="text-[11px] text-stone-400">
            {walls.length} wall{walls.length === 1 ? '' : 's'} · {Math.round(walls.reduce((s, w) => s + Number(w.width_ft || 0) * Number(w.height_ft || 0), 0))} sq ft total
            {savedAt && <span className="ml-2 text-green-400">✓ saved</span>}
          </span>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg bg-stone-700 px-4 py-2 text-sm text-white hover:bg-stone-600">Close</button>
            <button data-testid="wallpaper-hang-list-save" onClick={save} disabled={saving} className="rounded-lg bg-[#B45309] px-5 py-2 text-sm font-bold text-white hover:bg-[#92400E] disabled:opacity-60">
              {saving ? 'Saving…' : savedAt ? '✓ Saved' : 'Save hang list'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WallpaperHangList;
